/**
 * Theme Provider Component
 * Manages application theme state (dark, light, system)
 * Handles localStorage persistence and system preference detection
 */

import { createContext, useContext, useEffect, useState } from 'react';

// Available theme options
export type Theme = 'dark' | 'light' | 'system';

/**
 * Props for the ThemeProvider component
 */
export type ThemeProviderProps = {
  children: React.ReactNode; // Child components to wrap
  defaultTheme?: Theme; // Default theme if none stored
  storageKey?: string; // localStorage key for persistence
};

/**
 * Theme context state interface
 */
export type ThemeProviderState = {
  theme: Theme; // Current theme setting

  setTheme: (theme: Theme) => void; // Function to change theme
};

// Initial state for theme context
export const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

// Create theme context
export const ThemeProviderContext =
  createContext<ThemeProviderState>(initialState);

/**
 * Theme Provider Component
 * Provides theme management functionality to child components
 * Handles theme persistence and system preference detection
 *
 * @param children - Components to wrap with theme context
 * @param defaultTheme - Default theme setting (defaults to "system")
 * @param storageKey - Key for localStorage persistence
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  // Initialize theme from localStorage or use default
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  );

  // Apply theme changes to document root
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark');

    // Handle system theme preference
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    // Apply selected theme
    root.classList.add(theme);
  }, [theme]);

  // Context value with theme state and setter
  const value = {
    theme,
    setTheme: (theme: Theme) => {
      // Persist theme choice to localStorage
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Custom hook to access theme context
 * Must be used within a ThemeProvider component
 *
 * @returns ThemeProviderState - Current theme and setter function
 * @throws Error if used outside of ThemeProvider
 */
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
